import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import { bmoApi } from '../services/api';
import { colors, getScoreColor, KAYAKALP_PASS_SCORE, KK_THEMES } from '../theme';
import { Card, StatCard, ScoreBadge, ProgressBar, AlertBanner, SectionHeader, Spinner, EmptyState } from '../components/common';

function FacilityScoreRow({ facility, onClick }) {
  const color = getScoreColor(facility.score_percentage ?? 0);
  const passing = (facility.score_percentage ?? 0) >= KAYAKALP_PASS_SCORE;
  return (
    <tr
      onClick={onClick}
      style={{ cursor:'pointer', borderBottom:`1px solid ${colors.neutral[100]}` }}
      onMouseEnter={(e) => e.currentTarget.style.background = colors.neutral[50]}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <td style={{ padding:'12px 16px', fontSize:14, fontWeight:500, color: colors.text.primary }}>{facility.name}</td>
      <td style={{ padding:'12px 16px', fontSize:12, color: colors.text.secondary }}>{facility.type_label}</td>
      <td style={{ padding:'12px 16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <ProgressBar pct={facility.score_percentage ?? 0} />
          <span style={{ fontSize:14, fontWeight:700, color, minWidth:42 }}>{Math.round(facility.score_percentage ?? 0)}%</span>
        </div>
      </td>
      <td style={{ padding:'12px 16px' }}>
        <span style={{ fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:99, background: passing ? colors.success[50] : colors.error[50], color: passing ? colors.success[700] : colors.error[700] }}>
          {passing ? '✓ PASS' : '✗ FAIL'}
        </span>
      </td>
      <td style={{ padding:'12px 16px', fontSize:12, color: colors.text.secondary }}>{facility.last_updated || '—'}</td>
    </tr>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [sortBy,     setSortBy]     = useState('score_desc');

  useEffect(() => {
    bmoApi.getDashboard()
      .then(setData)
      .catch((err) => setError(err?.message || 'Could not load dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner size={48} />;
  if (error)   return <AlertBanner type="error" message={error} />;

  const facilities = data?.facilities || [];
  const alerts     = data?.alerts     || [];
  const trend      = data?.district_trend || [];

  const passing   = facilities.filter((f) => (f.score_percentage ?? 0) >= KAYAKALP_PASS_SCORE).length;
  const failing   = facilities.length - passing;
  const avgScore  = facilities.length
    ? facilities.reduce((s, f) => s + (f.score_percentage ?? 0), 0) / facilities.length
    : 0;

  const sorted = [...facilities].sort((a, b) => {
    if (sortBy === 'score_desc') return (b.score_percentage ?? 0) - (a.score_percentage ?? 0);
    if (sortBy === 'score_asc')  return (a.score_percentage ?? 0) - (b.score_percentage ?? 0);
    return a.name.localeCompare(b.name);
  });

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom:24 }}>
        <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:700, color: colors.text.primary }}>District Overview</h1>
        <p style={{ margin:0, fontSize:14, color: colors.text.secondary }}>
          {data?.block_name || 'Block'} · {data?.district_name || 'District'} · {facilities.length} facilities
        </p>
      </div>

      {/* Alerts */}
      {alerts.filter((a) => a.severity === 'HIGH').map((alert, idx) => (
        <AlertBanner key={idx} type="error" message={`⚠ ${alert.message}`} />
      ))}

      {/* Summary stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16, marginBottom:24 }}>
        <StatCard icon="🏥" label="Total Facilities"    value={facilities.length}         color={colors.primary[700]} />
        <StatCard icon="📊" label="District Avg Score"  value={`${Math.round(avgScore)}%`} color={getScoreColor(avgScore)} />
        <StatCard icon="✅" label="Passing (≥70%)"      value={passing}                    color={colors.success[700]} bg={colors.success[50]} />
        <StatCard icon="❌" label="Below Pass Mark"      value={failing}                    color={colors.error[700]}   bg={colors.error[50]}   />
        <StatCard icon="🔔" label="Active Alerts"        value={alerts.length}              color={colors.warning[700]} bg={colors.warning[50]} />
      </div>

      {/* Charts row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>

        {/* District score trend */}
        <Card>
          <SectionHeader title="District Score Trend" subtitle="Average score across all facilities" />
          {trend.length === 0
            ? <EmptyState icon="📈" title="No trend data" subtitle="Score history will appear here" />
            : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="month" tick={{ fontSize:12 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize:12 }} />
                  <Tooltip formatter={(v) => [`${v.toFixed(1)}%`, 'Avg Score']} />
                  <Line type="monotone" dataKey="avg_score" stroke={colors.primary[600]} strokeWidth={2} dot={{ r:4 }} />
                  <Line type="monotone" dataKey="pass_mark" stroke={colors.warning[500]} strokeWidth={1} strokeDasharray="4 4" dot={false} name="Pass mark" />
                </LineChart>
              </ResponsiveContainer>
            )
          }
        </Card>

        {/* Score distribution bar */}
        <Card>
          <SectionHeader title="Score Distribution" subtitle="Facilities by performance band" />
          {(() => {
            const bands = [
              { name:'Excellent (≥90%)', count: facilities.filter((f) => (f.score_percentage ?? 0) >= 90).length, color: colors.score.excellent },
              { name:'Good (75–89%)',    count: facilities.filter((f) => (f.score_percentage ?? 0) >= 75 && (f.score_percentage ?? 0) < 90).length, color: colors.score.good },
              { name:'Average (50–74%)',count: facilities.filter((f) => (f.score_percentage ?? 0) >= 50 && (f.score_percentage ?? 0) < 75).length, color: colors.score.average },
              { name:'Poor (<50%)',      count: facilities.filter((f) => (f.score_percentage ?? 0) < 50).length, color: colors.score.poor },
            ];
            return (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={bands} layout="vertical">
                  <XAxis type="number" tick={{ fontSize:12 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize:11 }} />
                  <Tooltip formatter={(v) => [`${v} facilities`]} />
                  <Bar dataKey="count" radius={[0,4,4,0]}>
                    {bands.map((b, i) => (
                      <rect key={i} fill={b.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </Card>
      </div>

      {/* Facility table */}
      <Card style={{ padding:0 }}>
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${colors.divider}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <SectionHeader title="All Facilities" subtitle={`${facilities.length} facilities in block`} />
          <div style={{ display:'flex', gap:8 }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ padding:'6px 10px', border:`1px solid ${colors.neutral[300]}`, borderRadius:6, fontSize:13, color: colors.text.primary }}
            >
              <option value="score_desc">Score: High → Low</option>
              <option value="score_asc">Score: Low → High</option>
              <option value="name">Name A–Z</option>
            </select>
            <button
              onClick={() => navigate('/compare')}
              style={{ padding:'6px 14px', background: colors.primary[700], color: colors.white, border:'none', borderRadius:6, fontSize:13, cursor:'pointer' }}
            >
              ⚖️ Compare
            </button>
          </div>
        </div>

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background: colors.neutral[50] }}>
                {['Facility', 'Type', 'Score', 'Status', 'Last Updated'].map((h) => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:12, fontWeight:600, color: colors.text.secondary, textTransform:'uppercase', letterSpacing:0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0
                ? <tr><td colSpan={5}><EmptyState icon="🏥" title="No facilities found" /></td></tr>
                : sorted.map((f) => (
                    <FacilityScoreRow
                      key={f.id}
                      facility={f}
                      onClick={() => navigate(`/facilities/${f.id}`)}
                    />
                  ))
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
